/**
 * Application entry point.
 * Hash-based routing with auth check.
 */
import './styles/main.css';
import { checkAuth } from './services/auth';
import { route, startRouter } from './services/router';
import { renderLoginScreen } from './screens/login';
import { renderFoldersScreen } from './screens/folders';
import { renderWorksheetListScreen } from './screens/list';
import { renderAdminScreen } from './screens/admin';
import { renderActorCatalog } from './screens/admin-actors';
import { renderControlCatalog } from './screens/admin-controls';
import { renderTemplateMgmt } from './screens/admin-templates';
import { renderUserMgmt } from './screens/admin-users';
import { renderFolderMgmt } from './screens/admin-folders';
import { renderReports } from './screens/reports';
import { renderAppHeader } from './components/app-header';
import { initTheme, initThemeToggle } from './components/theme-toggle';
import { setCurrentFolder } from './services/state';

/** Wait for server to be ready (handles race condition on startup). */
async function waitForServer(maxRetries = 5, delayMs = 1000): Promise<boolean> {
  for (let i = 0; i < maxRetries; i++) {
    try {
      const res = await fetch('/api/health');
      if (res.ok) return true;
    } catch { /* server not ready */ }
    if (i < maxRetries - 1) await new Promise((r) => setTimeout(r, delayMs));
  }
  return false;
}

async function init(): Promise<void> {
  const app = document.getElementById('app');
  if (!app) return;

  // Apply theme early (no UI elements yet), defer zoom controls until after auth
  initTheme();

  const serverReady = await waitForServer();
  if (!serverReady) {
    app.innerHTML = `
      <div style="display:flex;align-items:center;justify-content:center;height:100vh;color:var(--colorError);font-size:14px">
        <div style="text-align:center">
          <p style="margin-bottom:8px">Failed to connect to the server.</p>
          <p style="font-size:12px;color:var(--colorTextMuted)">Make sure the server is running on port 3015.</p>
          <button onclick="window.location.reload()" style="margin-top:16px;padding:8px 16px;border-radius:6px;cursor:pointer;border:1px solid var(--colorBorder);background:var(--colorBgCard);color:var(--colorText)">Retry</button>
        </div>
      </div>`;
    return;
  }

  const user = await checkAuth();
  if (!user) {
    // Reset zoom on login screen — zoom only applies inside the app
    app.style.transform = '';
    app.style.width = '';
    app.style.height = '';
    await renderLoginScreen(app);
    return;
  }

  // Now that user is authenticated, init zoom controls + theme toggle
  initThemeToggle();

  // Persistent layout: header + content area
  app.innerHTML = '<div id="app-content" style="flex:1;overflow-y:auto"></div>';
  app.style.cssText = 'display:flex;flex-direction:column;height:100vh';
  renderAppHeader(app);
  const content = document.getElementById('app-content')!;

  // Register routes — all render into the content area below the header
  route('folders', () => renderFoldersScreen(content));

  route('folder/:id', ({ id }) => {
    setCurrentFolder(id);
    renderWorksheetListScreen(content);
  });

  route('folder/:id/reports', ({ id }) => {
    setCurrentFolder(id);
    renderReports(content, id);
  });

  route('admin', () => renderAdminScreen(content));

  route('admin/actors', () => {
    renderAdminSubScreen(content, 'Actor Catalog', renderActorCatalog);
  });

  route('admin/controls', () => {
    renderAdminSubScreen(content, 'Controls & Actions', renderControlCatalog);
  });

  route('admin/templates', () => {
    renderAdminSubScreen(content, 'Templates', renderTemplateMgmt);
  });

  route('admin/users', () => {
    renderAdminSubScreen(content, 'Users', renderUserMgmt);
  });

  route('admin/folders', () => {
    renderAdminSubScreen(content, 'Folders', renderFolderMgmt);
  });

  route('admin/folders/:folderId', ({ folderId }) => {
    renderAdminSubScreen(content, 'Folders', (el) => renderFolderMgmt(el, folderId));
  });

  startRouter();
}

/** Render an admin sub-screen with back navigation and no app header. */
function renderAdminSubScreen(
  container: HTMLElement,
  title: string,
  renderFn: (el: HTMLElement) => Promise<void> | void
): void {
  container.innerHTML = `
    <div style="max-width:1000px;margin:0 auto;padding:12px 24px">
      <div style="display:flex;align-items:center;gap:10px;margin-bottom:12px">
        <button id="admin-sub-back" style="color:var(--colorTextLight);background:none;border:none;cursor:pointer;padding:0;line-height:0;flex-shrink:0">
          <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7"/></svg>
        </button>
        <span style="font-size:14px;font-weight:600;color:var(--colorTextHeading);flex-shrink:0">${title}</span>
        <div id="admin-sub-toolbar" style="display:flex;align-items:center;gap:8px;margin-left:auto"></div>
      </div>
      <div id="admin-sub-content"></div>
    </div>
  `;

  document.getElementById('admin-sub-back')?.addEventListener('click', () => {
    window.location.hash = 'admin';
  });

  const content = document.getElementById('admin-sub-content')!;
  renderFn(content);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
